/* eslint-disable require-await */
import { reads } from '@ember/object/computed';
import Service, { service, type Registry as Services } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type User from 'register-partners-js/models/user';

// session related data maybe extracted to session service

interface UserCredentials {
  email?: string;
  password?: string;
  code?: string;
  loginToken?: string;
}

/**
 * User manager
 *
 * @class UserManager
 * @module @qonto/qonto-register-partners/services/user-manager
 * @extends Ember.Service
 */
export default class UserManagerService extends Service {
  @service declare session: Services['session'];
  // @ts-expect-error: module is not typed
  @service store;

  @tracked redirectUponLogout: string | null = null;

  @tracked currentUser: User | null = null;

  // COMPUTED
  @reads('session.data.authenticated.websocket_token') declare websocketToken: string;
  @reads('session.data.authenticated.expires') declare sessionExpirationTime: string | number;

  @reads('currentUser.memberships') declare userMemberships: unknown[];

  /**
   * Authentication
   *
   * @public
   * @method setup
   */
  async setup(credentials: UserCredentials = {}): Promise<User | undefined> {
    const { email, password, code, loginToken } = credentials;

    if (!this.session.isAuthenticated && !email && !loginToken) {
      return;
    }

    const data = loginToken ? { loginToken } : { email, password, code };

    if (!this.session.isAuthenticated) {
      await this.session.authenticate('authenticator:custom', data);
    }

    const user = await this.loadUser();

    return user;
  }

  /**
   * Load the informations of a user
   *
   * @private
   * @method loadUser
   */
  async loadUser(): Promise<User> {
    const user: User = await this.store.queryRecord('user', {});
    await this._setCurrentUser(user);
    return user;
  }

  // PRIVATES

  /**
   * Set the current user
   *
   * @private
   * @method _setCurrentUser
   */
  async _setCurrentUser(user: User): Promise<void> {
    this.set('currentUser', user);
  }
}

declare module '@ember/service' {
  interface Registry {
    userManager: UserManagerService;
    'user-manager': UserManagerService;
  }
}
